import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import DealerWrapper from "./layouts/DealerWrapper";
import AuthLayout from "./layouts/AuthLayout";
import ProtectedRoute from "./routes/ProtectedRoute";
import AdminLayout from "./layouts/AdminLayout";
import AdminProtectedRoute from "./routes/AdminProtectedRoute";
import ContentManagerProtectedRoute from "./routes/ContentManagerProtectedRoute";
import ContentManagerLayout from "./layouts/ContentManagerLayout";
import TitleManager from "./components/TitleManager";
import { Toaster } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';
import ClaimPage from "./pages/ClaimPage";
import DealerEditPage from "./pages/DealerEditPage";
import { AdminLoader } from "./components/common/AdminLoader";

// -------------------- LAZY LOADED PAGES --------------------

// Base Pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
// const Leads = lazy(() => import("./pages/Leads"));
// const Services = lazy(() => import("./pages/Services"));
// const LocatorSettings = lazy(() => import("./pages/LocatorSettings"));
const ComingSoonLocator = lazy(() => import("./pages/ComingSoonLocator"));
// const Gallery = lazy(() => import("./pages/Gallery"));
const ListingImage = lazy(() => import("./pages/ListingImage"));
// const SocialLinks = lazy(() => import("./pages/SocialLinks"));
const Profile = lazy(() => import("./pages/Profile"));
const Addresses = lazy(() => import("./pages/Addresses"));
const SavedPayments = lazy(() => import("./pages/SavedPayments"));
const Orders = lazy(() => import("./pages/Orders"));

const DealerProgram = lazy(() => import("./pages/DealerProgram"));
const ProfitCalculator = lazy(() => import("./pages/ProfitCalculator"));
const SalesResources = lazy(() => import("./pages/SalesResources"));
const DigitalArt = lazy(() => import("./pages/DigitalArt"));
const UserManual = lazy(() => import("./pages/UserManual"));
const Register = lazy(() => import("./pages/Register"));
const Login = lazy(() => import("./pages/Login"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const GmbCallback = lazy(() => import("./pages/GmbCallback"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TestError = lazy(() => import("./pages/TestError"));

// Content Manager Pages
const ContentManagerLogin = lazy(() => import("./pages/content-manager/ContentManagerLogin"));
const ContentManagerPageManagement = lazy(() => import("./pages/content-manager/page-management/Index"));
const ContentManagerEditPage = lazy(() => import("./pages/content-manager/page-management/EditPage"));

// Admin Pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminDealers = lazy(() => import("./pages/admin/AdminDealers"));
const AdminBacklinks = lazy(() => import("./pages/admin/AdminBacklinks"));
const AdminTiers = lazy(() => import("./pages/admin/AdminTiers"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminDealersByTier = lazy(() => import("./pages/admin/AdminDealersByTier"));
const AdminDealerDetails = lazy(() => import("./pages/admin/AdminDealerDetails"));
const AdminFeedbacks = lazy(() => import("./pages/admin/AdminFeedbacks"));
const AdminDealerPortalFeedback = lazy(() => import("./pages/admin/AdminDealerPortalFeedback"));
const AdminOnboarding = lazy(() => import("./pages/admin/AdminOnboarding"));
const AdminPageManagement = lazy(() => import("./pages/admin/page-management/AdminPageManagement"));
const ClaimRequest = lazy(() => import("./pages/admin/page-management/ClaimRequest"));
const RemovalRequest = lazy(() => import("./pages/admin/page-management/RemovalRequest"));
const AdminDealerEditPage = lazy(() => import("./pages/admin/page-management/AdminDealerEditPage"));
const ProfileUpdateRequests = lazy(() => import("./pages/admin/profile-update-request/request-list"));
const ContentEditRequests = lazy(() => import("./pages/admin/content-edit-request/ContentEditRequests"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));

// Admin Analytics Sub-pages
const ActiveDealersAnalytics = lazy(() => import("./pages/admin/analytics/ActiveDealersAnalytics"));
const TrafficInsights = lazy(() => import("./pages/admin/analytics/TrafficInsights"));
const TopLocations = lazy(() => import("./pages/admin/analytics/TopLocations"));
const BacklinkHealth = lazy(() => import("./pages/admin/analytics/BacklinkHealth"));
const DealerActivityLogs = lazy(() => import("./pages/admin/analytics/DealerActivityLogs"));


const PageLoader = () => <AdminLoader message="Loading page..." />;

function App() {
  return (
    <>
      <BrowserRouter>
        <TitleManager />
        <Toaster richColors position="top-center" />
        <ErrorBoundary><Suspense fallback={<PageLoader />}>
          <Routes>
            {/* -------------------- AUTH LAYOUT PAGES -------------------- */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/register" element={<Register />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/test-error" element={<TestError />} />
              <Route path="/content-manager/login" element={<ContentManagerLogin />} />
            </Route>
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/gmb/callback" element={<GmbCallback />} />

            {/* <Route path="/dealer-locator" element={<DealerLocator />} /> */}

            {/* Redirect root */}
            <Route path="/" element={<Navigate to="/dashboard" />} />

            {/* -------------------- PROTECTED + LAYOUT PAGES -------------------- */}
            <Route
              element={
                <ProtectedRoute>
                  <DealerWrapper />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/coming-soon-locator" element={<ComingSoonLocator />} />
              <Route path="/locator-settings" element={<ComingSoonLocator />} />
              <Route path="/leads" element={<ComingSoonLocator />} />
              <Route path="/gallery" element={<ComingSoonLocator />} />
              <Route path="/services" element={<ComingSoonLocator />} />
              <Route path="/social" element={<ComingSoonLocator />} />

              {/* My Account Routes */}
              <Route path="/listing-image" element={<ListingImage />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/addresses" element={<Addresses />} />
              <Route path="/saved-payment-methods" element={<SavedPayments />} />
              <Route path="/orders" element={<Orders />} />

              <Route path="/dealer-program" element={<DealerProgram />} />
              <Route path="/profit-calculator" element={<ProfitCalculator />} />
              <Route path="/sales-resources" element={<SalesResources />} />
              <Route path="/digital-art" element={<DigitalArt />} />
              <Route path="/manual" element={<UserManual />} />
              <Route path="/claim-page" element={<ClaimPage />} />
              <Route path="/page-edit/:id" element={<DealerEditPage />} />
              {/* <Route path="/testing" element={<Testing />} /> */}
            </Route>

            {/* -------------------- ADMIN ROUTES -------------------- */}
            <Route
              path="/admin"
              element={
                <AdminProtectedRoute>
                  <AdminLayout />
                </AdminProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="page-management">
                <Route index element={<AdminPageManagement />} />
                <Route path="claim-requests" element={<ClaimRequest />} />
                <Route path="removal-requests" element={<RemovalRequest />} />
                <Route path="edit-page/:id" element={<AdminDealerEditPage />} />
              </Route>
              <Route path="analytics">
                <Route index element={<Navigate to="active-dealers" replace />} />
                <Route path="active-dealers" element={<ActiveDealersAnalytics />} />
                <Route path="traffic" element={<TrafficInsights />} />
                <Route path="locations" element={<TopLocations />} />
                <Route path="backlinks" element={<BacklinkHealth />} />
                <Route path="activity" element={<DealerActivityLogs />} />
                <Route path="activity-logs" element={<Navigate to="/admin/analytics/activity" replace />} />
              </Route>
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="dealers" element={<AdminDealers />} />
              <Route path="onboarding" element={<AdminOnboarding />} />
              <Route path="backlinks" element={<AdminBacklinks />} />
              <Route path="tiers" element={<AdminTiers />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="dealers-by-tier" element={<AdminDealersByTier />} />
              <Route path="dealers/:id" element={<AdminDealerDetails />} />
              <Route path="feedbacks">
                <Route index element={<Navigate to="/admin/feedbacks/locator" replace />} />
                <Route path="locator" element={<AdminFeedbacks />} />
                <Route path="portal" element={<AdminDealerPortalFeedback />} />
              </Route>
              <Route path="profile-update-requests" element={<ProfileUpdateRequests />} />
              <Route path="content-requests" element={<ContentEditRequests />} />
            </Route>

            {/* -------------------- CONTENT MANAGER ROUTES -------------------- */}
            <Route
              path="/content-manager"
              element={
                <ContentManagerProtectedRoute>
                  <ContentManagerLayout />
                </ContentManagerProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/content-manager/page-management" replace />} />
              <Route path="page-management" element={<ContentManagerPageManagement />} />
              <Route path="page-management/edit-page/:id" element={<ContentManagerEditPage />} />
              <Route path="content-requests" element={<ContentEditRequests isContentManager={true} />} />
            </Route>

            {/* 404 Fallback Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter >
    </>
  );
}

export default App;
